import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import jwt_decode from 'jwt-decode';
import { Observable } from 'rxjs';
import { API_PATH } from 'src/environments/environment';
import { IUser } from '../../../domain/registration/models/IUser';
import { IAction } from 'src/app/domain/IAction';
import { PartnerRoleTypeEnum } from 'src/app/domain/enums/partner-role-type.enum';
import { hasLegacyRoleFallback } from 'src/app/shared/permissions/legacy-role-fallback';



@Injectable({
  providedIn: 'root',
})
export class AuthService {
  constructor(private http: HttpClient) { }

  public login(user: any) {
    return this.http.post<any>(`${API_PATH}main-auth`, user);
  }

  public tenantLogin(credentials: any) {
    return this.http.post<any>(`${API_PATH}main-auth/tenant-login`, credentials);
  }

  public lookupTenantLogin(credentials: any) {
    return this.http.post<any>(`${API_PATH}main-auth/lookup`, credentials);
  }

  public selectTenantUser(credentials: any) {
    return this.http.post<any>(`${API_PATH}main-auth/select`, credentials);
  }

  public logout() {
    const token = this.getAuthorizationToken();

    window.localStorage.removeItem('@gmt:permissions');
    window.localStorage.removeItem('@gmt:tenant_id');
    window.localStorage.removeItem('@gmt:login_slug');

    return this.http.delete(`${API_PATH}main-auth/${token}`);
  }


  public createAccount(account: any) {
    return new Promise((resolve) => resolve(true));
  }

  public getAuthorizationToken() {
    const token = window.localStorage.getItem('@gmt:access_token');
    return token;
  }

  /**
   * Tenant do usuário logado, lido do token (igual ABCX) — usado pelo
   * interceptor para enviar o header x-tenant-id.
   */
  public getTenantId(): string | null {
    const storedTenantId = window.localStorage.getItem('@gmt:tenant_id');
    if (storedTenantId) return storedTenantId;

    const token = this.getAuthorizationToken();
    if (!token) return null;
    try {
      const decoded: any = jwt_decode(token);
      return decoded?.tenantId || null;
    } catch {
      return null;
    }
  }

  public getCurrentUser() {
    try {
      const userStr = window.localStorage.getItem('@gmt:user');
      if (!userStr) return null;
      return JSON.parse(userStr);
    } catch (error) {
      console.error('Erro ao fazer parse do usuário:', error);
      return null;
    }
  }

  public getUserId() {
    const user = JSON.parse(window.localStorage.getItem('@gmt:user') || '');
    return user.id;
  }

  public getUser(): IUser | null {
    const user = window.localStorage.getItem('@gmt:user');
    return user != null ? JSON.parse(user) : user;
  }

  public getPartner() {
    const user = JSON.parse(window.localStorage.getItem('@gmt:user') || '');
    const partner = user?.partner;

    if (partner) {
      partner.isPartner = !!partner.isPartner;
      partner.isConsultant = !!partner.isConsultant;
      partner.isRepresentative = !!partner.isRepresentative;
      partner.isCounter = !!partner.isCounter;
      partner.isUnit = !!partner.isUnit;
    }

    return partner;
  }

  public isMerchant() {
    const user = JSON.parse(window.localStorage.getItem('@gmt:user') || '');
    if(user && user.nameGroupPermission){
        return user.nameGroupPermission === "MERCHANT";
    }

    return false;
  }

  
  public isFinancial() {
    const user = JSON.parse(window.localStorage.getItem('@gmt:user') || '');
    if(user && user.nameGroupPermission){
        return user.nameGroupPermission === "FINANCIAL";
    }

    return false;
  }

  public getPartnerId(): string | null {
    const partner = this.getPartner();
    return partner ? partner.id : null;
  }

  public isPartner(): boolean {
    try {
      const userStr = window.localStorage.getItem('@gmt:user');
      if (!userStr) {
        return false;
      }

      const user = JSON.parse(userStr || '{}');
      const isPartner = user?.partner?.id ? true : false;

      // console.log('isPartner check:', isPartner, user?.partner);
      return isPartner;
    } catch (error) {
      return false;
    }
  }

  public getUserName(): string {
    const user = this.getCurrentUser();
    return user ? user.firstName : 'Usuário desconhecido';
  }


  getUserNameById(userId: string): string {
    const currentUser = this.getCurrentUser(); // Obtém o usuário atual do localStorage
    return currentUser && currentUser.id === userId ? currentUser.firstName : 'Usuário desconhecido';
  }


  public getTokenExpirationDate(token: string): Date | undefined {
    const decoded: any = jwt_decode(token);

    if (decoded.exp === undefined) return undefined;

    const date = new Date(0);

    date.setUTCSeconds(decoded.exp);

    return date;
  }

  public isTokenExpired(token: string, offsetSeconds?: number) {
    if (token === null || token === '') return true;

    const date = this.getTokenExpirationDate(token);

    offsetSeconds = offsetSeconds || 0;

    if (date === undefined) return false;

    return !(date.valueOf() > new Date().valueOf() + offsetSeconds * 1000);
  }

  public isUserLoggedIn() {
    const token = this.getAuthorizationToken();
    if (!token) return false;
    else if (this.isTokenExpired(token)) return false;

    return true;
  }

  /**
   * Renova a sessao usando o REFRESH TOKEN (nao o access token vigente, que
   * pode estar prestes a expirar ou ja expirado). O backend valida a
   * assinatura/claim do refresh token e emite um novo par access+refresh;
   * a rotacao invalida o refresh token anterior, entao o novo precisa ser
   * persistido pelo caller (ver auth-interceptor.ts).
   */
  public refreshMainToken() {
    const token = window.localStorage.getItem('@gmt:refresh_token');
    return this.http.post<any>(`${API_PATH}main-auth/refresh`, { token });
  }

  /**
   * Envia e-mail de recuperacao de senha (self-service, sem autenticacao).
   */
  public recoverPassword(email: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      `${API_PATH}users/forgot-password`,
      { email }
    );
  }

  /**
   * Redefine a senha com base no token recebido por e-mail.
   * Contrato real do endpoint: PUT /users/reset-password {token, password}
   */
  public resetPassword(token: string, password: string): Observable<any> {
    return this.http.put<any>(`${API_PATH}users/reset-password`, {
      token,
      password
    });
  }

  /**
   * Troca a senha da sessao ativa. Requer a senha atual para confirmacao.
   * Endpoint: PUT /users/me/password (self: o backend usa o usuario do token, sem id na rota).
   */
  public changeOwnPassword(
    payload: { password: string; newPassword: string; confirmPassword: string }
  ): Observable<any> {
    return this.http.put<any>(`${API_PATH}users/me/password`, payload);
  }

  /**
   * Lista as filiais ativas acessiveis ao usuario logado, com a filial selecionada.
   * Endpoint: GET /users/branch-options
   */
  public listBranchOptions(): Observable<{ options: Array<{ id: string; name: string; isActive: boolean }>; selectedBranchId: string | null }> {
    return this.http.get<any>(`${API_PATH}users/branch-options`);
  }

  /**
   * Grava a filial selecionada para a sessao do usuario logado.
   * Endpoint: PUT /users/select-branch
   */
  public selectBranch(branchId: string): Observable<any> {
    return this.http.put<any>(`${API_PATH}users/select-branch`, { branchId });
  }

  /**
   * Gate de menu/tela: role propria (`<ROLE>_SHOW|_ENABLE` ou a role crua) OU, na
   * transicao (Regra N1), a role legada da qual ela foi desmembrada — ver
   * shared/permissions/legacy-role-fallback.ts. Industria/Faturamento/Caixa/Fiscal (telas futuras)
   * herdam de OS/Inicio so para quem ja existia (grandfather fase 1: `legacyScreenAlias` do login);
   * perfil novo ou chave WAZIR_PERMISSIONS_V2 ON = so a role propria.
   */
  private hasRolePermission(
    permissions: string[],
    role: string,
    suffix: '_SHOW' | '_ENABLE'
  ): boolean {
    const fullRole = `${role}${suffix}`;
    if (permissions.includes(role) || permissions.includes(fullRole)) {
      return true;
    }
    return hasLegacyRoleFallback(permissions, fullRole, {
      legacyScreenAlias: this.getCurrentUser()?.legacyScreenAlias !== false,
    });
  }

  public isShow(action: IAction): boolean {
    const user = this.getCurrentUser();
    if (user) {
      const permissions = user.permissions;
      if (permissions) {
        return this.hasRolePermission(permissions, action.role, '_SHOW');
      }
    }

    return false;
  }

  public isEnable(action: IAction): boolean {
    const user = this.getCurrentUser();
    if (user) {
      const permissions = user.permissions;
      if (permissions) {
        return this.hasRolePermission(permissions, action.role, '_ENABLE');
      }
    }

    return false;
  }

  /**
   * Retorna todos os papéis do parceiro logado
   */
  public getPartnerRoleTypes(): PartnerRoleTypeEnum[] {
    const partner = this.getPartner();
    const roles: PartnerRoleTypeEnum[] = [];

    if (partner) {
      if (partner.isPartner) roles.push(PartnerRoleTypeEnum.PARTNER);
      if (partner.isConsultant) roles.push(PartnerRoleTypeEnum.CONSULTANT);
      if (partner.isRepresentative) roles.push(PartnerRoleTypeEnum.REPRESENTATIVE);
      if (partner.isCounter) roles.push(PartnerRoleTypeEnum.COUNTER);
      if (partner.isUnit) roles.push(PartnerRoleTypeEnum.UNIT);
    }

    return roles.length > 0 ? roles : [PartnerRoleTypeEnum.UNKNOWN];
  }

  /**
   * Retorna o papel primário do parceiro logado, com base em ordem de prioridade
   */
  public getPrimaryPartnerRole(): PartnerRoleTypeEnum {
    const roles = this.getPartnerRoleTypes();
    const priorityOrder: PartnerRoleTypeEnum[] = [
      PartnerRoleTypeEnum.CONSULTANT,
      PartnerRoleTypeEnum.PARTNER,
      PartnerRoleTypeEnum.REPRESENTATIVE,
      PartnerRoleTypeEnum.COUNTER,
      PartnerRoleTypeEnum.UNIT,
    ];

    for (const role of priorityOrder) {
      if (roles.includes(role)) return role;
    }

    return PartnerRoleTypeEnum.UNKNOWN;
  }

  public getPartnerConsultantId(): string | null {
    const partner = this.getPartner();
    return partner ? partner.consultantId : null;
  }

  public getPartnerRepresentativeId(): string | null {
    const partner = this.getPartner();
    return partner ? partner.representativeId : null;
  }

  /**
   * Verifica se o usuário logado pertence aos grupos DEVELOPER, ADMIN ou SUPORT (case insensitive, nomes alternativos)
   * Fallback temporário: permite para dev@dev.com.br se não houver userGroups (apenas para dev/test)
   */
  public isDeveloperOrAdmin(): boolean {
    try {
      const user = this.getCurrentUser();
      // Fallback temporário para ambiente de desenvolvimento
      if (
        user &&
        (!user.userGroups || !Array.isArray(user.userGroups)) &&
        user.email &&
        user.email.toLowerCase() === 'dev@dev.com.br'
      ) {
        return true;
      }
      if (!user || !user.userGroups || !Array.isArray(user.userGroups)) return false;

      return user.userGroups.some((group: any) => {
        const name = (group.name || '').toString().toLowerCase();
        return (
          name === 'developer' ||
          name === 'admin' ||
          name === 'adm' ||
          name === 'enterprise_adm' ||
          name === 'root' ||
          name === 'dev' ||
          name === 'suport' ||
          name === 'support' ||
          name.includes('developer') ||
          name.includes('admin') ||
          name.includes('enterprise_adm') ||
          name.includes('root') ||
          name.includes('dev') ||
          name.includes('suport') ||
          name.includes('support')
        );
      });
    } catch (error) {
      console.error('Erro ao verificar grupos do usuário:', error);
      return false;
    }
  }

  /**
   * Verifica se o usuário logado pertence especificamente ao grupo SUPPORT
   */
  public isSupportUser(): boolean {
    try {
      const user = this.getCurrentUser();
      if (!user || !user.userGroups || !Array.isArray(user.userGroups)) return false;

      return user.userGroups.some((group: any) => {
        const name = (group.name || '').toString().toLowerCase();
        return (
          name === 'support' ||
          name === 'suport' ||
          name.includes('support') ||
          name.includes('suport')
        );
      });
    } catch (error) {
      console.error('Erro ao verificar se usuário é do grupo SUPPORT:', error);
      return false;
    }
  }
}
